import React, {useEffect, useState, useRef, useMemo} from 'react'
import Head from 'next/head'
import {useRouter} from 'next/router'
import {VStack, HStack} from '@components/Stack'
import {usePaddleLoad} from 'src/hooks/usePaddleLoad'
import {ProductEnum, Products} from 'src/products'
import {useUser} from 'src/hooks/useUser'
import {subscribeToCourseDetails} from 'src/util/db'
import {StartLearnModal} from 'src/learn/StartLearnModal'
import styles from 'src/Learn.module.css'
import {LearnSubscribeModal} from 'src/learn/LearnSubscribeModal'
import {CourseTile} from 'src/learn/CourseTile'
import {toast} from 'react-toastify'
import {sendEvent} from 'src/analytics'
import {InnerPageHeader} from '@components/InnerPageHeader'
import {Panel} from '@components/Panel'
import {Text} from '@components/Text'
import {Icon} from 'src/Icon'
import {Testimonial} from '@components/Testimonial'
import {Modal} from '@components/Modal'
import {Button} from 'src/Button'
import {showConfetti} from 'src/utils'
import {CountDown} from 'src/CountDown'
import {FreeCourseModal} from 'src/learn/FreeCourseModal'
import {LeaderboardStat} from '@components/LeaderboardStat'
import {AnimatePresence, motion} from 'framer-motion'

const LearnCssPage = ({country}) => {
  const router = useRouter()
  const hasVendorScriptLoaded = usePaddleLoad(50177)
  const user = useUser()
  const paymentProvider = country === 'IN' ? 'dodo' : 'paddle'

  const [courseDetails, setCourseDetails] = useState()
  const [isCourseDetailFetched, setIsCourseDetailFetched] = useState(false)
  const [courseBeingPurchased, setCourseBeingPurchased] = useState(null)
  const courseBeingPurchasedRef = useRef(null)

  function updateCourseBeingPurchased(key) {
    courseBeingPurchasedRef.current = key
    setCourseBeingPurchased(key)
  }

  useEffect(() => {
    if (!user?.uid) return

    subscribeToCourseDetails(user.uid, (data) => {
      setIsCourseDetailFetched(true)
      setCourseDetails(data)

      // purchase was successful and backend key created
      if (
        courseBeingPurchasedRef.current &&
        data[courseBeingPurchasedRef.current]
      ) {
        updateCourseBeingPurchased(null)
        showConfetti(2)
      }
    })
  }, [user?.uid])

  const [isIntermediateHelpModalOpen, setIsIntermediateHelpModalOpen] =
    useState(false)

  const [hasForcedBuyQueryParam, setHasForcedBuyQueryParam] = useState(false)
  useEffect(() => {
    if (!router.isReady) return () => {}

    setHasForcedBuyQueryParam(router.query.buy)
  }, [router.isReady])

  const buyBtnHandler = (type) => {
    const key = {
      [ProductEnum.LEARN_BASIC]: 'basic',
      [ProductEnum.LEARN_INTERMEDIATE]: 'intermediate',
    }[type]

    if (!user?.uid) {
      toast.info("You'll need to login first. Taking you to login page...")

      // Cache current path so that after login user comes back here
      window.localStorage.setItem('beforeLoginLocation', '/learn')

      setTimeout(() => {
        router.push('/login')
      }, 2000)
      return
    }
    sendEvent({name: 'courseCheckoutStarted', label: key})
    const referral = window.Rewardful && window.Rewardful.referral
    const passthrough = {userId: user.uid}
    if (referral) {
      passthrough.rewardful = {referral}
    }
    if (paymentProvider === 'paddle') {
      window.Paddle.Checkout.open({
        product: Products[type].vendorId,
        passthrough: JSON.stringify(passthrough),
        successCallback: () => {
          updateCourseBeingPurchased(key)
          sendEvent({name: 'coursePurchased', label: key})
          // window.location.reload()
        },
      })
    } else if (paymentProvider === 'dodo') {
      window.location.href = `https://checkout.dodopayments.com/buy/${
        Products[type].dodoProductId
      }?quantity=1&redirect_url=${
        window.location.origin + window.location.pathname
      }&fullName=${user.displayName}&email=${
        user?.firebaseUser?.email
      }&metadata_passthrough=${JSON.stringify(passthrough)}`
    }
  }

  const [isStartModalOpen, setIsStartModalOpen] = useState(false)
  const [isFreeCourseModalOpen, setIsFreeCourseModalOpen] = useState(false)
  const [isSubscribeOpen, setIsSubscribeOpen] = useState(false)
  const [courseTypeForSubscription, setCourseTypeForSubscription] = useState('')

  const [playLink, setPlayLink] = useState(false)

  const start = ({link}) => {
    setIsStartModalOpen(true)
    setPlayLink(link)
  }

  const saleTag = (
    <span style={{color: '#eee', fontWeight: 500}} className="new-font">
      <span style={{fontWeight: 600, color: 'limegreen'}}>End of Month</span>{' '}
      SALE 🚀
    </span>
  )

  const countdownEndDate = new Date('Mar 2 2025 05:30:00 GMT+0530')
  const isCountdownActive = useMemo(() => {
    return countdownEndDate > new Date()
  }, [countdownEndDate])
  const countDown = isCountdownActive ? (
    <HStack justify="center">
      <VStack gap="2rem" align="center">
        <HStack align="center" gap="0.4rem">
          {saleTag} ends in
        </HStack>
        <CountDown showEmoji={false} date={countdownEndDate} suffix="" />
      </VStack>
    </HStack>
  ) : null

  return (
    <>
      <Head>
        <meta
          key="url"
          property="og:url"
          content="https://cssbattle.dev/learn"
        />

        <meta
          key="banner"
          property="og:image"
          content={`https://cssbattle.dev/learnSocialImage2.png`}
        />

        <meta key="ogTitle" property="og:title" content="CSSBattle's LEARN" />
        <title key="title">Learn CSS with game-like courses | CSSBattle</title>

        <meta
          key="desc"
          property="og:description"
          content="Learn CSS - the gamified way!"
        />
      </Head>
      <InnerPageHeader icon="learn" title="Learn CSS" />
      <VStack gap="4rem" align="center">
        <VStack gap="1rem" align="center">
          <Text
            align="center"
            mono={false}
            size={2}
            weight={500}
            tag="p"
            maxChars={60}
          >
            CSSBattle's LEARN is the first-ever gamified CSS course targeted
            towards beginners, intermediates, and even experts. Each course
            consists of multiple levels which you play and learn - just like a
            game!
          </Text>

          <section style={{marginTop: '3rem'}} id="enroll">
            <Testimonial
              name="Kevin Powell"
              role="CSS Evangelist"
              image={['kevin-powell.png', 'kevin-powell.jpeg']}
              link="https://x.com/KevinJPowell"
              showQuoteIcon={false}
            >
              This is a lot of fun. Nice interactive way to start learning!
            </Testimonial>
          </section>
        </VStack>
        <section>
          <VStack gap="2rem" align="flex-start">
            <HStack gap="2rem" align="stretch" responsive>
              <CourseTile
                id="basic"
                glowing
                image="/images/learn/beginner.png"
                title="Beginner Levels"
                description="Start your journey here by learning about the basics of CSS and the most commonly used properties"
                stats={
                  <div className={styles.statsContainer}>
                    <ChapterStat
                      courseType="basic"
                      title="Beginner Chapters"
                      chapters={[
                        'Sizing',
                        'Background',
                        'Opacity',
                        'Borders',
                        'Box shadow',
                        'Filters',
                        'Transform',
                        'Text color',
                        'Padding',
                        'Box sizing',
                        'Selectors',
                      ]}
                    />
                    <div className={styles.statsBox}>
                      <p>70+</p>
                      <p>Fun levels</p>
                    </div>
                  </div>
                }
                icon={
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      background: 'var(--clr-brand)',
                    }}
                  ></div>
                }
                isAvailable
                disabled={
                  !hasVendorScriptLoaded || (user && !isCourseDetailFetched)
                }
                vendorId={Products[ProductEnum.LEARN_BASIC].vendorId}
                nonDiscountedPrice={`US${
                  Products[ProductEnum.LEARN_BASIC].price
                }`}
                demoLink="/learn/demo/1"
                onPlay={
                  courseDetails?.basic && !hasForcedBuyQueryParam
                    ? () =>
                        start({
                          link: `/learn/basic/${courseDetails.basic.level}`,
                        })
                    : null
                }
                onBuy={() => buyBtnHandler(ProductEnum.LEARN_BASIC)}
                isPurchaseInProgress={courseBeingPurchased === 'basic'}
              />
              <CourseTile
                id="intermediate"
                glowing
                nonDiscountedPrice={`US${
                  Products[ProductEnum.LEARN_INTERMEDIATE].price
                }`}
                image="/images/learn/intermediate.png"
                title="Intermediate Levels"
                description="Move a step forward and become a master of layouts with some exciting new fun-filled levels"
                icon={
                  <svg width="50" height="64" fill="none">
                    <path fill="#870" d="M10 30h30v33H10z"></path>
                    <path fill="#BDA500" d="M5 20h40v36H5z"></path>
                    <path fill="#FFDF00" d="M0 0h50v50H0z"></path>
                  </svg>
                }
                stats={
                  <div className={styles.statsContainer}>
                    <ChapterStat
                      courseType="intermediate"
                      title="Intermediate Chapters"
                      chapters={[
                        'Logical properties',
                        'Units',
                        'Selectors & Specificity',
                        'Box model - II',
                        'Display & Flow layout',
                        'Background - II',
                        'Overflow',
                        'Positioning',
                        'Z-index',
                        'Flexbox',
                      ]}
                    />
                    <div className={styles.statsBox}>
                      <p>300+</p>
                      <p>Fun levels</p>
                    </div>
                  </div>
                }
                demoLink="/learn/demo/1"
                isAvailable
                disabled={
                  !hasVendorScriptLoaded || (user && !isCourseDetailFetched)
                }
                vendorId={Products[ProductEnum.LEARN_INTERMEDIATE].vendorId}
                onPlay={
                  courseDetails?.intermediate && !hasForcedBuyQueryParam
                    ? () =>
                        start({
                          link: `/learn/intermediate/${courseDetails.intermediate.level}`,
                        })
                    : null
                }
                onBuy={() => buyBtnHandler(ProductEnum.LEARN_INTERMEDIATE)}
                isPurchaseInProgress={courseBeingPurchased === 'intermediate'}
              />
              <CourseTile
                image="/images/learn/pro.png"
                title="Expert Levels"
                description="Learn about some advanced CSS concepts, and know when and how to use these techniques"
                icon={
                  <svg
                    width="59"
                    height="59"
                    viewBox="0 0 59 59"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="39.3333" height="39.3333" fill="#887700" />
                    <path
                      d="M0 0H39.3333L59 19.6667H19.6667L0 0Z"
                      fill="#BDA500"
                    />
                    <path
                      d="M0 39.3335H39.3333L59 59.0002H19.6667L0 39.3335Z"
                      fill="#BDA500"
                    />
                    <rect
                      width="39.3333"
                      height="39.3333"
                      transform="translate(19.6667 19.6665)"
                      fill="#FFDF00"
                    />
                  </svg>
                }
                onBuy={() => {
                  setCourseTypeForSubscription('advanced')
                  setIsSubscribeOpen(true)
                }}
              />
            </HStack>
            <HStack justify="center" fullWidth gap="3rem">
              <HStack gap="1rem">
                <LeaderboardStat
                  title="Players"
                  value="3000+"
                  icon="learn"
                  description="Learners"
                />
              </HStack>

              <Button
                cool
                onClick={() => {
                  setIsFreeCourseModalOpen(true)
                  sendEvent({name: 'freeCourseModalOpened'})
                }}
              >
                <HStack gap="0.5rem" align="center">
                  <Icon name="dollar" marginRight="0" />
                  Get the course for FREE!
                  <Icon name="dollar" marginRight="0" />
                </HStack>
              </Button>

              <HStack gap="1rem">
                <LeaderboardStat
                  title="Players"
                  value="70%"
                  icon="trophy"
                  description="Completion rate"
                />
              </HStack>
            </HStack>

            <Panel padding="1rem 1.5rem" fullWidth>
              <HStack justify="center">
                <Text
                  mono={false}
                  weight={500}
                  size="1"
                  appearance="secondary"
                  letterSpacing="0.3px"
                >
                  All courses get free lifetime updates for new and updated
                  levels! The price is exclusive of local taxes.
                </Text>
              </HStack>
            </Panel>
          </VStack>
        </section>

        {countDown}

        <HStack gap="3rem" align="flex-start" fullWidth responsive>
          <div style={{flexGrow: 1}}>
            <VStack gap="3rem" align="flex-start">
              <VStack gap="1rem" align="flex-start">
                <Text mono={false} size="3-5" weight={600} appearance="primary">
                  What's special about CSSBattle courses?
                </Text>

                <div>
                  <Text
                    mono={false}
                    size="2"
                    weight={500}
                    appearance="secondary"
                  >
                    Imagine learning CSS, not through tedious videos or complex
                    articles, but with an exciting, game-like experience. It's a
                    learning adventure where you don't just learn, you play,
                    engage, and absorb knowledge at a rapid-fire pace. It's like
                    turning your grind into a thrilling quest, except the loot
                    you get is tangible CSS prowess! And that is why our courses
                    have a never-seen completion rate of 70%!
                  </Text>
                </div>
              </VStack>
              <VStack gap="2rem" align="flex-start">
                <Text mono={false} size="3-5" weight={600} appearance="primary">
                  FAQ
                </Text>
                <VStack gap="1rem" align="flex-start">
                  <Faq question="Whom is the Basic course for?">
                    <p>
                      If you have no knowledge of CSS or have just started with
                      learning CSS, CSS Learn Basic is the course for you. You
                      just need a very basic knowledge of{' '}
                      <a
                        className="link"
                        href="https://developer.mozilla.org/en-US/docs/Learn/HTML/Introduction_to_HTML/Getting_started"
                        target="_blank"
                        rel="noopener"
                      >
                        HTML
                      </a>
                      , that's it!
                    </p>
                    <p>
                      <strong>🚨 Note</strong>: This course won't teach you
                      code-golfing concepts and thus getting better score on
                      CSSBattle. It's meant to learn CSS.
                    </p>
                  </Faq>

                  <Faq question="Whom is the Intermediate course for?">
                    <p>
                      If you have some knowledge of CSS and want to learn more
                      to get ready for the professional world, CSS Learn
                      Intermediate is the course for you.
                    </p>
                    <p>
                      <strong>🚨 Note</strong>: This course won't teach you
                      code-golfing concepts and thus getting better score on
                      CSSBattle. It's meant to learn CSS.
                    </p>
                  </Faq>

                  <Faq question="What do I get to learn in the courses?">
                    <p>The chapters of each course are outlined above.</p>
                  </Faq>

                  <Faq question="Can I try the courses before buying?">
                    <p>
                      Yes, we have a FREE chapter to try. Just hit the "Demo"
                      button in the course tiles above. Note: It's the same demo
                      chapter in both the courses, so click and try any.
                    </p>
                  </Faq>

                  <Faq question="Is this a subscription or one-time cost?">
                    <p>
                      You have to pay one-time for lifetime access to the
                      course.
                    </p>
                  </Faq>

                  <Faq question="Is there some bulk-purchase or team discount?">
                    <p>
                      We can figure something out. Message us on our{' '}
                      <a className="link" href="mailto:contact@cssbattle.dev">
                        mail
                      </a>
                      .
                    </p>
                  </Faq>

                  <Faq question="I have some other question">
                    <p>
                      Sure, let us know on our{' '}
                      <a className="link" href="mailto:contact@cssbattle.dev">
                        mail
                      </a>
                      .
                    </p>
                  </Faq>
                </VStack>
              </VStack>
            </VStack>
          </div>

          <VStack align="flex-start" gap="2.5rem">
            <HStack gap="0.75rem">
              <Icon
                name="quote"
                marginRight="0"
                size="32"
                color="var(--clr-brand)"
              />
              <Text mono={false} size="3-5" weight={600} appearance="primary">
                Hear it from the best
              </Text>
            </HStack>

            <Testimonial
              name="Kirupa"
              role="Founder of Kirupa.com"
              image="kirupa.jpeg"
              link="https://www.kirupa.com/"
            >
              This is a great way for beginners to learn CSS! Anything that adds
              gamification and entices people to go deeper without feeling like
              a "chore" is a great thing!
            </Testimonial>

            <Testimonial
              name="Dhhyey Desai"
              role="Online Instructor (500K+ Students)"
              image="dhhyey.jpeg"
              link="https://dhhyey.me"
            >
              A very interactive course compared to those available from other
              providers, which feel more like lectures. With an interactive and
              in-browser embedded coding environment, what more could you ask
              for? I highly recommend this course to beginners or even coders
              with some experience who are trying to get back into CSS!
            </Testimonial>
          </VStack>
        </HStack>

        <HStack justify="center">
          <Testimonial
            name="Coinnich"
            role="Student"
            showQuoteIcon={true}
            image="default.png"
          >
            <p>
              Thanks to this succinct CSS course I have now been using some of
              the less used features of CSS like flex-grow: 1. I have finally
              used inset after trying it a few times and going back to the top,
              right, bottom and left properties instead. The same is true of the
              ch unit which I have used several times now and has proven very
              useful, for container queries in particular. There is also a
              section on overflow which was really amazing as it is a topic that
              I usually managed to avoid!
            </p>

            <p>
              This is a very interactive tutorial which encourages you to engage
              with the material and also has quizzes to ensure that you
              conceptually understand the subject, which with CSS I personally
              think is crucial! So give this tutorial a go and learn much more
              than just the subjects mentioned above.
            </p>
          </Testimonial>
        </HStack>

        <VStack gap="2rem" align="center">
          <HStack justify="center">
            <a
              className="button button--huge button--cssbattle button--animated-icon"
              href="#enroll"
            >
              <HStack gap="0.25rem">
                Enroll now
                <span className="icon">🚀</span>
              </HStack>
            </a>
          </HStack>
        </VStack>

        <VStack align="flex-start" gap="2.5rem" fullWidth={true}>
          <HStack gap="0.75rem">
            <Icon
              name="star"
              marginRight="0"
              size="32"
              color="var(--clr-brand)"
            />
            <Text mono={false} size="3-5" weight={600} appearance="primary">
              How is this course different?
            </Text>
          </HStack>
          <HStack gap="3rem" justify="space-between" fullWidth responsive>
            <Feature index={1} title="Code all-along">
              It's not your usual read or watch. You get to code all along.
            </Feature>
            <Feature index={2} title="Understand & Advance">
              You play through gamified levels, progressing only when you clear
              the current level.
            </Feature>
            <Feature index={3} title="Quizzes">
              Tons of quizzes to ensure you understand & practice the concepts.
            </Feature>
          </HStack>
        </VStack>

        <VStack align="flex-start" fullWidth gap="2rem">
          <HStack gap="0.75rem">
            <Icon
              name="quote"
              marginRight="0"
              size="32"
              color="var(--clr-brand)"
            />
            <Text mono={false} size="3-5" weight={600} appearance="primary">
              From our course takers...
            </Text>
          </HStack>

          <HStack gap="3rem" fullWidth responsive>
            <Testimonial name="Alys" role="UI Designer" showQuoteIcon={true}>
              The course is fresh and fast, it allows you to refresh your memory
              on parts of CSS that you now take for granted." -
            </Testimonial>
            <Testimonial
              name="Ralph Pastel"
              role="Product manager"
              showQuoteIcon={true}
            >
              The best way to break out tutorial hell by actually practicing!
              And it is quite fun, which makes it even better.
            </Testimonial>
            <Testimonial
              name="Gustavo Milczwski"
              role="Tech lead"
              showQuoteIcon={true}
            >
              This was the most enjoyable way to learn (and relearn) CSS in the
              right way. I can't wait for the expert levels!
            </Testimonial>
          </HStack>

          <HStack gap="3rem" justify="Center" fullWidth responsive>
            <Testimonial name="Joshua" role="Student" showQuoteIcon={true}>
              The course was a lot of fun. When I finished I was sad that it was
              over. I already had a basic understanding of css but brushing up
              on it was still fun and engaging.
            </Testimonial>
          </HStack>
        </VStack>

        <VStack gap="2rem" align="center">
          <HStack justify="center">
            <a
              className="button button--huge button--cssbattle button--animated-icon"
              href="#enroll"
            >
              <HStack gap="0.25rem">
                Enroll now
                <span className="icon">🚀</span>
              </HStack>
            </a>
          </HStack>
          {countDown}
        </VStack>
      </VStack>

      <Modal isOpen={isIntermediateHelpModalOpen}>
        <VStack>
          <Text
            tag="h2"
            mono={false}
            size="4"
            weight={700}
            appearance="primary"
          >
            Pre-ordering Intermediate Course
          </Text>
          <Text tag="p" mono={false} size={1}>
            Few things to know before pre-ordering the Intermediate course:
          </Text>

          <ul className={styles.textList}>
            <li>
              Pre-ordering gives you an early version preview of the
              Intermediate course, covering 3 chapters: Display, Positioning &
              Flexbox.
            </li>

            <li>
              When the full course is out, you will be able to access all the
              chapters by just coming back here.
            </li>

            <li>
              Full course includes 200+ levels covering following chapters:
              Display, Positioning, Overflow, z-index, Units, Logical
              properties, Functions, Selectors, Pseudo classes n elements,
              Flexbox, Backgrounds & Accessibility.
            </li>

            <li>We'll notify you by email when the full course is released.</li>
          </ul>

          <HStack gap="1rem">
            <Button
              onClick={() => {
                setIsIntermediateHelpModalOpen(false)
              }}
            >
              Later
            </Button>
            <Button
              onClick={() => {
                setIsIntermediateHelpModalOpen(false)
                buyBtnHandler(ProductEnum.LEARN_INTERMEDIATE)
              }}
              extraClasses={`button--cssbattle`}
            >
              Let's do it
            </Button>
          </HStack>
        </VStack>
      </Modal>

      <StartLearnModal
        isOpen={isStartModalOpen}
        link={playLink}
        onRequestClose={() => setIsStartModalOpen(false)}
      />

      <LearnSubscribeModal
        isOpen={isSubscribeOpen}
        onRequestClose={() => setIsSubscribeOpen(false)}
        type={courseTypeForSubscription}
      />

      <FreeCourseModal
        isOpen={isFreeCourseModalOpen}
        onRequestClose={() => setIsFreeCourseModalOpen(false)}
      />
    </>
  )
}

const H2 = ({children}) => (
  <h2 style={{color: '#FEE333', fontSize: '1.7rem'}}>{children}</h2>
)
const H3 = ({children}) => (
  <h3 style={{color: '#FEE333', fontSize: '1.4rem'}}>{children}</h3>
)

function Faq({question, children}) {
  // const slug = `#${convertToSlug(question)}`
  const [isOpen, setIsOpen] = useState(false)

  return (
    <details className="faq-section" open={isOpen}>
      <summary className="faq-section__question">
        <Text mono={false} size="1" weight={500} appearance="primary">
          {question}
          <a href={''} className="faq-section__question-link">
            #
          </a>
        </Text>
      </summary>
      <div className="faq-section__answer">
        <Text mono={false} size="1" weight={500} appearance="secondary">
          {children}
        </Text>
      </div>
    </details>
  )
}

function ChapterStat({chapters = [], title, courseType}) {
  const [isChapterModalOpen, setIsChapterModalOpen] = useState(false)

  const openModal = () => {
    setIsChapterModalOpen(true)
    sendEvent({name: 'courseOutlineOpened', label: courseType})
  }

  return (
    <>
      <button className={styles.statsBox} onClick={openModal}>
        <p>{chapters.length}</p>
        <p>Chapters</p>
      </button>
      <AnimatePresence>
        {isChapterModalOpen && (
          <motion.div
            initial={{opacity: 0, y: 50}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0}}
            className={styles.chapterModal}
          >
            <Panel classes={styles.chapterModal}>
              <VStack gap="2rem">
                <Text
                  tag="h2"
                  mono={false}
                  size="3"
                  weight={700}
                  appearance="primary"
                >
                  {title}
                </Text>
                <ol>
                  <VStack gap="0.3rem" align="flex-start">
                    {chapters.map((chapter, index) => (
                      <li key={index}>
                        <Text
                          mono={false}
                          size="2"
                          weight={500}
                          appearance="primary"
                        >
                          {chapter}
                        </Text>
                      </li>
                    ))}
                  </VStack>
                </ol>
                <Button onClick={() => setIsChapterModalOpen(false)} primary>
                  Close
                </Button>
              </VStack>
            </Panel>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function Feature({title, index, children}) {
  return (
    <div className={styles.feature}>
      <span className={styles.featureNumber}>{index}</span>
      <Panel classes={styles.featureCard}>
        <VStack gap="0.5rem" align="flex-start">
          <Text
            tag="h3"
            mono={false}
            size="4"
            weight={600}
            appearance="primary"
          >
            {title}
          </Text>
          <Text tag="p" mono={false} size="1" weight={500}>
            {children}
          </Text>
        </VStack>
      </Panel>
    </div>
  )
}

export async function getServerSideProps(ctx) {
  const country = ctx.req.headers['x-vercel-ip-country']
  console.log('ip', ctx.req.headers)
  if (!country) {
    return {props: {}}
  }
  return {
    props: {
      country: country,
    },
  }
}

export default LearnCssPage
